import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'
import { AlertTriangle, Eye } from 'lucide-react'

import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { Card } from '@/common/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { formatNumber } from '@/common/lib/utils'
import useQueryGetPartner from '@/modules/manage-player/api/hooks/useQueryGetPartner'
import useQueryGetPartnerPaymentRecords from '@/modules/manage-player/api/hooks/useQueryGetPartnerPaymentRecords'
import PaymentRecordDetailDialog from '@/modules/manage-player/components/PartnerPlayers/components/PaymentRecordDetailDialog'
import CreatePaymentRecordDialog from '@/modules/manage-player/components/PartnerPlayers/components/CreatePaymentRecordDialog'

// Component to show latest payment record for a partner
function PartnerLatestPayment({ partnerId }: { partnerId: string }) {
	const { data, isLoading } = useQueryGetPartnerPaymentRecords(partnerId, {
		skip: 0,
		take: 1,
	})

	const latestRecord = data?.data?.[0]

	if (isLoading) {
		return <Skeleton className="h-4 w-24" />
	}

	if (!latestRecord) {
		return <span className="text-muted-foreground">-</span>
	}

	const paymentDate = parseISO(latestRecord.createdAt)
	const daysSincePayment = Math.floor(
		(Date.now() - paymentDate.getTime()) / (1000 * 60 * 60 * 24),
	)
	const colorClass =
		daysSincePayment <= 30 ? 'text-green-600' : 'text-yellow-600'

	return (
		<span className={colorClass}>
			{format(paymentDate, 'd MMM yy', { locale: th })}
		</span>
	)
}

function PartnerPaymentsPage() {
	const { pageSize, currentPage } = useTablePagination()
	const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(
		null,
	)
	const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
	const [detailDialogOpen, setDetailDialogOpen] = useState(false)
	const [paymentListDialogOpen, setPaymentListDialogOpen] = useState(false)

	const payload = {
		skip: (currentPage - 1) * pageSize,
		take: pageSize,
	}

	const { data, isLoading } = useQueryGetPartner(payload)

	const handleViewPayments = (partnerId: string) => {
		setSelectedPartnerId(partnerId)
		setPaymentListDialogOpen(true)
	}

	return (
		<PageContainer>
			<Card>
				<div className="space-y-4 p-4">
					<div>
						<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
							ประวัติจ่ายส่วนแบ่งพาร์ทเนอร์
						</h2>
						<p className="text-sm text-muted-foreground">
							จัดการและดูประวัติการจ่ายส่วนแบ่งให้กับพาร์ทเนอร์
						</p>
					</div>

					<div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
						<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
						<div className="space-y-1">
							<p className="font-medium">วิธีการจ่ายส่วนแบ่งให้พาร์ทเนอร์</p>
							<p>แอดมินสามารถจ่ายส่วนแบ่งให้พาร์ทเนอร์ได้ 2 ช่องทาง ดังนี้</p>
							<ul className="ml-4 list-disc space-y-0.5">
								<li>
									<strong>จ่ายโดยไม่ผ่านระบบ</strong> —
									จ่ายส่วนแบ่งให้พาร์ทเนอร์โดยตรง ด้วยการโอนเงินหรือจ่ายเงินสด
								</li>
								<li>
									<strong>เพิ่มเครดิตในระบบ</strong> —
									เพิ่มเครดิตเข้ากระเป๋าเงินพาร์ทเนอร์
									โดยพาร์ทเนอร์สามารถถอนเองได้ผ่านช่องทางการถอนเงินของระบบ
								</li>
							</ul>
							<p className="pt-1">
								<strong>หลังจากจ่ายด้วยวิธีใดก็ตาม</strong>{' '}
								แอดมินมีหน้าที่บันทึกประวัติการจ่ายส่วนแบ่งลงในระบบทุกครั้ง
								เพื่อให้ข้อมูลถูกต้องและสามารถตรวจสอบย้อนหลังได้
							</p>
						</div>
					</div>

					{isLoading ? (
						<div className="space-y-3">
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
							<Skeleton className="h-10 w-full" />
						</div>
					) : (
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>ชื่อเล่น</TableHead>
										<TableHead>เปิดใช้งาน</TableHead>
										<TableHead>รหัสแนะนำ</TableHead>
										<TableHead className="text-right">เปอร์เซ็นต์</TableHead>
										<TableHead>จ่ายล่าสุด</TableHead>
										<TableHead className="text-right">การดำเนินการ</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data?.data.map((partner) => (
										<TableRow key={partner.id}>
											<TableCell className="font-medium">
												{partner.nickname}
											</TableCell>
											<TableCell>
												{partner.isActive ? (
													<Badge variant="default" className="text-xs">
														เปิดอยู่
													</Badge>
												) : (
													<Badge variant="secondary" className="text-xs">
														ปิดอยู่
													</Badge>
												)}
											</TableCell>
											<TableCell>{partner.referralCode}</TableCell>
											<TableCell className="text-right">
												{(partner.percentage * 100).toFixed(1)}%
											</TableCell>
											<TableCell>
												<PartnerLatestPayment partnerId={partner.id} />
											</TableCell>
											<TableCell className="text-right">
												<div className="flex items-center justify-end gap-2">
													<CreatePaymentRecordDialog
														partnerId={partner.id}
														partnerNickname={partner.nickname}
													/>
													<Button
														variant="outline"
														size="sm"
														onClick={() => handleViewPayments(partner.id)}
													>
														<Eye className="mr-1 h-4 w-4" />
														ดูประวัติ
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}

					{/* Pagination info */}
					<div className="flex items-center justify-end text-sm text-muted-foreground">
						แสดง {data?.data.length || 0} จาก {data?.total || 0} รายการ
					</div>
				</div>
			</Card>

			{/* Payment List Dialog */}
			{selectedPartnerId && (
				<PaymentListDialog
					open={paymentListDialogOpen}
					onOpenChange={setPaymentListDialogOpen}
					partnerId={selectedPartnerId}
					onViewDetail={(recordId) => {
						setSelectedRecordId(recordId)
						setDetailDialogOpen(true)
					}}
				/>
			)}

			{/* Payment Detail Dialog */}
			{selectedPartnerId && (
				<PaymentRecordDetailDialog
					open={detailDialogOpen}
					onOpenChange={setDetailDialogOpen}
					partnerId={selectedPartnerId}
					recordId={selectedRecordId}
				/>
			)}
		</PageContainer>
	)
}

// Payment List Dialog Component
function PaymentListDialog({
	open,
	onOpenChange,
	partnerId,
	onViewDetail,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	partnerId: string
	onViewDetail: (recordId: string) => void
}) {
	const { data, isLoading } = useQueryGetPartnerPaymentRecords(partnerId, {
		skip: 0,
		take: 50,
	})

	const records = data?.data || []

	return (
		<div>
			{open && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className="fixed inset-0 bg-black/50"
						onClick={() => onOpenChange(false)}
					/>
					<div className="relative z-50 max-h-[80vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-background p-6 shadow-lg">
						<div className="mb-4 flex items-center justify-between">
							<h3 className="text-lg font-semibold">ประวัติการจ่ายส่วนแบ่ง</h3>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onOpenChange(false)}
							>
								✕
							</Button>
						</div>

						{isLoading ? (
							<div className="space-y-3">
								<Skeleton className="h-10 w-full" />
								<Skeleton className="h-10 w-full" />
								<Skeleton className="h-10 w-full" />
							</div>
						) : records.length === 0 ? (
							<div className="py-8 text-center text-muted-foreground">
								ยังไม่มีประวัติการจ่ายส่วนแบ่ง
							</div>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>ช่วงเวลา</TableHead>
											<TableHead className="text-right">
												จำนวนเงินที่จ่าย
											</TableHead>
											<TableHead className="text-right">ผู้เล่น</TableHead>
											<TableHead className="text-right">ส่วนแบ่ง</TableHead>
											<TableHead>ผู้บันทึก</TableHead>
											<TableHead>วันที่บันทึก</TableHead>
											<TableHead></TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{records.map((record) => {
											const startDate = format(
												parseISO(record.startDate),
												'd MMM yy',
												{ locale: th },
											)
											const endDate = format(
												parseISO(record.endDate),
												'd MMM yy',
												{ locale: th },
											)
											const createdAt = format(
												parseISO(record.createdAt),
												'd MMM yy HH:mm',
												{ locale: th },
											)
											const snapshot = record.snapshotData

											return (
												<TableRow key={record.id}>
													<TableCell className="font-medium">
														{startDate} - {endDate}
													</TableCell>
													<TableCell className="text-right font-bold text-green-600">
														{formatNumber(record.amount)}
													</TableCell>
													<TableCell className="text-right">
														{snapshot?.summary?.playerCount || 0}
													</TableCell>
													<TableCell className="text-right font-medium text-primary">
														{formatNumber(
															snapshot?.summary?.totalPartnerShare || 0,
														)}
													</TableCell>
													<TableCell className="text-muted-foreground">
														{record.createdByUsername || '-'}
													</TableCell>
													<TableCell className="text-muted-foreground">
														{createdAt}
													</TableCell>
													<TableCell>
														<Button
															variant="ghost"
															size="sm"
															onClick={() => onViewDetail(record.id)}
														>
															<Eye className="h-4 w-4" />
														</Button>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

const GuardedPartnerPaymentsPage = withUserPermission(PartnerPaymentsPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_PLAYER],
})

export default GuardedPartnerPaymentsPage
